import { setCmsAdvanced } from "@/api/cms-puck/api";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

/**
 * Aiguillage du CMS avancé — réglage PAR INSTANCE (porté par `CMS`).
 * Visible uniquement en mode MIXED. Activé : l'éditeur CMS avancé est accessible ET
 * le site public sert les pages CMS avancé. Désactivé : tout reste sur l'ancien CMS.
 */
export const CmsAdvancedModeToggle = () => {
  const cmsNew = useCmsNew();
  const { instanceData } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const accessMode = instanceData?.CMS?.cmsAccessMode ?? "MIXED";
  const enabled = instanceData?.CMS?.advancedCmsEnabled ?? false;

  const mutation = useMutation({
    mutationFn: (next: boolean) => setCmsAdvanced(next),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["instance"] });
      toast({
        title: data.advancedCmsEnabled
          ? cmsNew("cms.new.auto.title.cms_avance_active_pour_ce_site", "CMS avancé activé pour ce site")
          : cmsNew("cms.new.auto.title.cms_avance_desactive", "CMS avancé désactivé"),
        variant: "success",
        duration: 1600,
      });
    },
    onError: () => {
      toast({ title: cmsNew("cms.new.auto.title.echec_de_la_mise_a_jour", "Échec de la mise à jour"), variant: "error" });
    },
  });

  if (accessMode !== "MIXED") {
    return null;
  }

  return (
    <div className="flex items-start justify-between gap-6 rounded-lg border border-slate-200 p-4">
      <div className="space-y-1">
        <Label htmlFor="cms-advanced-mode" className="text-base font-semibold">
          {cmsNew("cms.new.auto.label.mode_avance_nouveau_cms_puck", "Mode avancé — nouveau CMS")}
        </Label>
        <p className="max-w-2xl text-sm text-slate-500">
          {cmsNew(
            "cms.new.auto.description.mode_avance_puck",
            "Active l'éditeur de pages nouvelle génération et le rendu public des pages CMS avancé pour ce site. Tant que c'est désactivé, l'ancien CMS reste seul en place. Réglage par site, réversible à tout moment.",
          )}
        </p>
      </div>
      <Switch
        id="cms-advanced-mode"
        checked={enabled}
        disabled={mutation.isPending}
        onCheckedChange={(v) => mutation.mutate(v)}
      />
    </div>
  );
};

export default CmsAdvancedModeToggle;
